import { useTranslation } from 'react-i18next';
import { lazy, Suspense } from 'react';
import { Navigate, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { Layout } from './components/Layout';

const Dashboard = lazy(() => import('./pages/Dashboard').then(module => ({ default: module.Dashboard })));
const Documents = lazy(() => import('./pages/Documents').then(module => ({ default: module.Documents })));
const DocumentDetail = lazy(() => import('./pages/DocumentDetail').then(module => ({ default: module.DocumentDetail })));
const Timeline = lazy(() => import('./pages/Timeline').then(module => ({ default: module.Timeline })));
const MapView = lazy(() => import('./pages/MapView').then(module => ({ default: module.MapView })));
const About = lazy(() => import('./pages/About').then(module => ({ default: module.About })));
const Apollo = lazy(() => import('./pages/Apollo').then(module => ({ default: module.Apollo })));
const ApolloLayerWorkbenchPage = lazy(() => import('./pages/Apollo').then(module => ({ default: module.ApolloLayerWorkbenchPage })));
const DaVinci = lazy(() => import('./pages/DaVinci').then(module => ({ default: module.DaVinci })));
const Michelangelo = lazy(() => import('./pages/Michelangelo').then(module => ({ default: module.Michelangelo })));
const Atlas = lazy(() => import('./pages/Atlas').then(module => ({ default: module.Atlas })));
const AtlasReader = lazy(() => import('./pages/AtlasReader').then(module => ({ default: module.AtlasReader })));
const AtlasExplorer = lazy(() => import('./pages/AtlasExplorer').then(module => ({ default: module.AtlasExplorer })));
const AtlasEyeMapper = lazy(() => import('./pages/AtlasEyeMapper').then(module => ({ default: module.AtlasEyeMapper })));
const AtlasAssets = lazy(() => import('./pages/AtlasAssets').then(module => ({ default: module.AtlasAssets })));
const AtlasStats = lazy(() => import('./pages/AtlasStats').then(module => ({ default: module.AtlasStats })));
const AtlasGraph = lazy(() => import('./pages/AtlasGraph').then(module => ({ default: module.AtlasGraph })));
const AtlasWrapLab = lazy(() => import('./pages/AtlasWrapLab').then(module => ({ default: module.AtlasWrapLab })));

// v2 — PURSUE standalone front. Coexists with v1; owns its own shell OUTSIDE
// the v1 <Layout>, mounted at /pursue/*. See src/pursue/.
const PursueApp = lazy(() => import('./pursue/PursueApp').then(module => ({ default: module.PursueApp })));

/** v1 — the original front. Every existing route, unchanged, inside <Layout>. */
function V1App() {
  const { t } = useTranslation();
  return (
    <Layout>
      <Suspense fallback={<div className="route-loading" aria-label={t('interfaceText.loading_dc380888')} />}>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/documents" element={<Documents />} />
          <Route path="/documents/:id" element={<DocumentDetail />} />
          <Route path="/timeline" element={<Timeline />} />
          <Route path="/map" element={<MapView />} />
          <Route path="/apollo" element={<Apollo />} />
          <Route path="/apollo/workbench" element={<ApolloLayerWorkbenchPage />} />
          <Route path="/apollo-17-layers" element={<Navigate to="/apollo/workbench" replace />} />
          <Route path="/davinci" element={<DaVinci />} />
          <Route path="/da-vinci" element={<Navigate to="/davinci" replace />} />
          <Route path="/da-vincis/*" element={<Navigate to="/davinci" replace />} />
          <Route path="/michelangelo" element={<Michelangelo />} />
          <Route path="/atlas" element={<Atlas />} />
          <Route path="/atlas/explorer" element={<AtlasExplorer />} />
          <Route path="/atlas/graph" element={<AtlasGraph />} />
          <Route path="/atlas/stats" element={<AtlasStats />} />
          <Route path="/atlas/eye-mapper" element={<AtlasEyeMapper />} />
          <Route path="/atlas/assets" element={<AtlasAssets />} />
          <Route path="/atlas/wrap-lab" element={<AtlasWrapLab />} />
          <Route path="/atlas/:slug" element={<AtlasReader />} />
          <Route path="/about" element={<About />} />
        </Routes>
      </Suspense>
    </Layout>
  );
}

export default function App() {
  const { t } = useTranslation();
  return (
    <ThemeProvider>
      <Routes>
        {/* v2 — PURSUE, its own shell, no v1 Layout */}
        <Route
          path="/pursue/*"
          element={
            <Suspense fallback={<div className="route-loading" aria-label={t('interfaceText.loading_dc380888')} />}>
              <PursueApp />
            </Suspense>
          }
        />
        {/* v1 — everything else */}
        <Route path="/*" element={<V1App />} />
      </Routes>
    </ThemeProvider>
  );
}
